import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core'
import { NgForm } from '@angular/forms'
import { EntityService } from '../../services/entity.service'
import { TableData } from '../../models/table-data'
import { Observable, BehaviorSubject, Subscription, of, Subject } from 'rxjs'
import { map, take, tap, switchMap } from 'rxjs/operators'
import { Store, select } from '@ngrx/store'
import { RootStoreState } from '../../../../root-store/root-index'
import * as fromConfigSelectors from '../../../../root-store/config-store/config.selectors'
import { Field } from '../../models/field'
import { SelectItem } from 'primeng/api'
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog'
import { InputText } from 'primeng/inputtext'
import { CatalogueService } from '../../../../services/catalogue.service'
import { SettingsService } from '../../../settings/services/settings.service'
import { BrowserCompatibilityService } from '../../../../services/browser-compatibility.service'
import { AdditionalQualifier } from '../../models/table-state.model'
import { EntityReference } from '../../models/entity-reference'
import { StoredEntityService } from '../../../entity-store-access/services/stored-entity.service'
import { SoftFilter } from '../../models/softfilter'
import { RichTextEditorComponent } from '../../../misc'
import StringUtils from 'projects/jml-lib/src/lib/utils/string.utils'
import { CronEditorDialogComponent } from '../cron-editor-dialog/cron-editor-dialog.component'
import { EntityConfiguration } from '../../models/entity-configuration'

/** Inputs:
 *  - data:
 *  - mainId: number      => id of the main entity (in case of entity attachments)
 *  - ownerType: string   => type of the owner of the attachment
 *  - ownerId: number     => id of the owner of the attachment
 *  - fields: Field[]     => fields to display
 *  - configType: string  => entity type
 *  - entity: Entity      => the entity itself to be displayed (= values for the fields)
 *  - entityId: number    => alternatively the entity id may be passed. In this case getEtntiy API will be called to retrieve the values.
 *  - entityRef: EntityRef => required forexecuting ScriptActions, especially for evaluating default values
 *  - description: string => a general description which will be shown above the form
 *  - initValues:         => defaultValues from code (instead of from DTOConfig)
 *  - buttonText:         => caption of comfirm button
 *  - header: string:   => dialog caption
 *  - width:            => width in px or %, eg. '500px' or '60%'
 */
@Component({
  selector: 'jml-lib-entity-dialog',
  templateUrl: './entity-dialog.component.html',
  styleUrls: ['./entity-dialog.component.scss'],
  providers: [DialogService]
})
export class EntityDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  catalogueOptions: Map<string, any[]> = new Map()
  entity: any
  mainId: number
  ownerType: string
  ownerId: number
  entityRef: EntityReference
  displayEntitySelectionDialog = false
  entitySelectionTableData: TableData
  entitySelectionContext: { field: string; textModule: any }
  codeSelCntxt: { field: Field; textModule: any }
  scrollPanelStyle
  defaultCache: Object = new Object()
  subscriptions: Subscription[] = []
  currency$: Observable<string>
  dtoConfigs: SelectItem[]
  syntax: string
  code: string
  showCodeEditor = false
  fields: Field[]
  configType: string
  configuration: EntityConfiguration
  description: string
  buttonText: string
  displayName: string
  dialogRef: DynamicDialogRef

  $onChange: Subject<Field[]>
  formEnabled = true

  //create custom localization for p-calendar component
  de: any

  filterTags = StringUtils.filterTags

  // Access the form with ViewChild
  @ViewChild('formFieldDiv', { static: true }) formContainer!: ElementRef

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private entityService: EntityService,
    private storedEntityService: StoredEntityService,
    private catalogueService: CatalogueService,
    private store$: Store<RootStoreState.State>,
    private settingsService: SettingsService,
    private dialogService: DialogService,
    public browserCompatibilityService: BrowserCompatibilityService
  ) {}

  ngOnInit() {
    //TODO: i18n verwenden! https://blog.ninja-squad.com/2019/12/10/angular-localize/
    this.de = {
      firstDayOfWeek: 1,
      dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
      dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      dayNamesMin: ['S', 'M', 'D', 'M', 'D', 'F', 'S'],
      monthNames: [
        'Januar',
        'Februar',
        'März',
        'April',
        'Mai',
        'Juni',
        'Juli',
        'August',
        'September',
        'Okotober',
        'November',
        'Dezember'
      ],
      monthNamesShort: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
      today: 'Heute',
      clear: 'Verwerfen'
    }

    // get fields to be displayed
    const data = this.config.data
    if (data['fields']) {
      this.fields = data['fields']
      this.scrollPanelStyle = {
        overflow: 'auto',
        width: '100%',
        'max-height': window.innerHeight * 0.6 + 'px',
        padding: '5px'
      }
    } else {
      console.log('[entity-dialog] no fields supplied')
      return
    }

    this.$onChange = data['onChange']

    // Re-enable form when receiving new events from observable
    if (data['enableForm'] && data['enableForm'] instanceof Observable<void>) {
      this.subscriptions.push(data['enableForm'].subscribe(() => (this.formEnabled = true)))
    }

    this.configType = !!data?.configType ? data.configType : data?.entityRef?.dtoType

    // get items for fields with type 'CatalogueEntry'
    this.fields.forEach((field) => {
      if (Field.isCatalogueEntry(field)) {
        if (!field.defaultCatalogue) {
          console.error(`Field ${field.field} is a CatalogueEntry but has no defaultCatalogue specified`)
        } else {
          this.subscriptions.push(
            this.catalogueService.getCatalogue(field.defaultCatalogue).subscribe((catalogue) => {
              const values = catalogue.values.map((e) => ({ label: e['name'], value: e['id'] }))
              this.catalogueOptions.set(field.defaultCatalogue, values)
            })
          )
        }
      }
    })

    // get Currency from settings
    this.currency$ = this.settingsService.getSetting('CURRENCY').pipe(map((setting) => setting.value))

    this.description = this.config.data['description']
    const initValues: any = this.config.data['initValues']

    // mainId required in case of subtypes (identifies its parent)
    this.mainId = data['mainId']

    // required for creation of generic attachments
    this.ownerType = data['ownerType']
    this.ownerId = data['ownerId']

    // caption of confirm button
    this.buttonText = data['buttonText']

    // get entity
    let $entity: Observable<any>
    if (data['entity']) {
      $entity = new BehaviorSubject(data['entity']).asObservable()
    } else if (data['entityId']) {
      $entity = this.storedEntityService.getEntity$(this.configType, data['entityId']).pipe(
        take(1),
        map((res) => res.fields)
      )
    }

    // entityRef required to evaulate defaults in case of parameter defaults for script execution
    this.entityRef = data?.entityRef

    if ($entity) {
      // get entity details
      this.subscriptions.push(
        $entity.subscribe((entity) => {
          this.entity = entity
          if (!!this.entity && !this.entityRef) {
            this.entityRef = new EntityReference(this.configType, this.entity['id'])
          }
        })
      )
    } else {
      // else: no entity specified => get default values
      // initValues beat defaults from config
      this.fields.forEach((field) => {
        if (initValues && initValues[field.field]) {
          this.defaultCache[field.field] = initValues[field.field]
        } else if (field.defaultValue) {
          if (!this.defaultCache.hasOwnProperty(field.field)) {
            if (field.defaultValue.startsWith('${')) {
              this.subscriptions.push(
                // this.entityRef is always undefined since there is no entity context
                this.entityService.eval(field.defaultValue, field.type, this.entityRef).subscribe((response) => {
                  this.defaultCache[field.field] = response['result']
                })
              )
            } else {
              this.defaultCache[field.field] = field.defaultValue
            }
          }
        }
        if (!this.defaultCache[field.field]) {
          this.defaultCache[field.field] = undefined
        }
      })
    }

    // get all dtoConfigs for field.type === 'dtoType' (e.g. for Script Actions)
    const configurations$ = this.store$.pipe(select(fromConfigSelectors.selectConfigs))
    this.subscriptions.push(
      configurations$.subscribe((configs) => {
        if (configs) {
          this.configuration = configs[this.configType]
          this.dtoConfigs = Object.values(configs).map((config) => this.configToSelectItem(config.type, config.type))
        }
      })
    )
  }

  ngAfterViewInit(): void {
    // Delay focus until after the next change detection cycle to ensure form stability
    setTimeout(() => this.focusFirstElement(), 100)
  }

  focusFirstElement(): void {
    // Get all focusable elements inside the form
    const focusableElements = this.formContainer.nativeElement.querySelectorAll('input, select, textarea')

    if (focusableElements.length > 0) {
      // Focus the first focusable element
      ;(focusableElements[0] as HTMLElement).focus()
    }
  }

  onWindowResize() {
    this.scrollPanelStyle = { overflow: 'auto', width: '100%', 'max-height': window.innerHeight * 0.6 + 'px' }
  }

  configToSelectItem(name: string, type: string): SelectItem {
    return { label: name, value: type }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
    this.dialogRef?.destroy()
  }

  entitySelected(entity: any, form: NgForm) {
    console.log('entitySelected', entity, form)
    if (!!entity) {
      form.control.patchValue({ [this.entitySelectionContext.field]: entity['id'] })
      this.entitySelectionContext.textModule['value'] = entity['_repr_']
      this.displayEntitySelectionDialog = false
    } else {
      console.log('tried to select null-entity')
    }
  }

  openEntitySelectionDialog(field: Field, input: InputText) {
    this.entitySelectionContext = { field: field.field, textModule: input }

    this.displayName = field.header
    let additionalQualifier
    if (field.referenceFilter) {
      additionalQualifier = new AdditionalQualifier(field.referenceFilter).setEntityRef(this.entityRef)
      if (this.mainId && this.configType) {
        additionalQualifier.setMainRef(this.mainId, this.configType)
      }
    }
    of(field.referenceSoftFilter)
      .pipe(
        take(1),
        switchMap((filter) => {
          if (filter) {
            // expression: string, resultFieldType?: string, entityReference?: EntityReference, context?: Map<String, any>, mainId?: number
            return this.entityService
              .eval(filter, undefined, this.entityRef, undefined, this.mainId)
              .pipe(map((x) => x.result))
          } else {
            return of(filter)
          }
        })
      )
      .subscribe((referenceSoftFilter) => {
        this.entitySelectionTableData = new TableData(field.referenceType)
          .isEntitySelectionDialog()
          .hideHeader()
          .hideAttachments()
          .setScrollable()
          .disableInlineEdit()
          .setScrollHeight(window.innerHeight - 350 + 'px')
          .setFilters(
            additionalQualifier,
            !!referenceSoftFilter ? new SoftFilter(this.entityRef, referenceSoftFilter) : undefined
          )
        this.displayEntitySelectionDialog = true
      })
  }

  openCodeEditor(field: Field, input: InputText, form: NgForm) {
    this.codeSelCntxt = { field: field, textModule: input }
    this.syntax = field.type
    this.code = form.control.get(this.codeSelCntxt.field.field).value
    this.showCodeEditor = true
  }

  onEdited(editedCode: string, form: NgForm) {
    if (editedCode) {
      form.control.patchValue({ [this.codeSelCntxt.field.field]: editedCode })
      this.codeSelCntxt.textModule['value'] = '<' + this.codeSelCntxt.field.type + '>*'
    } else {
      this.nullField(this.codeSelCntxt.field, form)
      this.codeSelCntxt.textModule['value'] = null
    }
    this.showCodeEditor = false
  }

  openMultilineEditor(field: Field, input: InputText, form: NgForm) {
    ;(<HTMLInputElement>(<unknown>input)).blur() // Prevents ExpressionChangedAfterItHasBeenCheckedError
    let entityValue = ''
    if (form.controls[field.field].value) {
      entityValue = form.controls[field.field].value
    } else {
      entityValue = this.defaultCache[field.field]
    }

    this.dialogRef = this.dialogService.open(RichTextEditorComponent, {
      data: {
        text: entityValue,
        saveButtonLabel: 'Übernehmen'
      },
      header: `${this.configuration.singleDisplayName} ${this.entity ? this.entity._repr_ : '(Neu)'}  - ${field.header}`,
      draggable: true,
      width: '80%'
    })

    this.dialogRef.onClose.pipe(take(1)).subscribe((text: string) => {
      if (text !== undefined) {
        form.control.patchValue({ [field.field]: text })
        input['value'] = this.filterTags(text)
      }
    })
  }

  openCronEditor(field: Field, input: InputText, form: NgForm) {
    ;(<HTMLInputElement>(<unknown>input)).blur() // Prevents ExpressionChangedAfterItHasBeenCheckedError
    let entityValue = ''
    if (form.controls[field.field].value) {
      //entityValue = form.controls[field.field].value;
      entityValue = input['value']
    } else {
      entityValue = this.defaultCache[field.field]
    }

    this.dialogRef = this.dialogService.open(CronEditorDialogComponent, {
      data: {
        cronExpression: entityValue,
        saveButtonLabel: 'Übernehmen'
      },
      header: field.header,
      draggable: true,
      width: '80%'
    })

    this.dialogRef.onClose.pipe(take(1)).subscribe((cronExpression: string) => {
      if (cronExpression !== undefined) {
        form.control.patchValue({ [field.field]: { cronExpression: cronExpression, type: 'CronTrigger' } })
        input['value'] = cronExpression
      }
    })
  }

  displayCron(field: string): string {
    const entity = this.entity ? this.entity : this.defaultCache
    if (entity[field]?.type === 'CronTrigger') {
      return entity[field].cronExpression
    } else if (entity[field]?.type === 'SimpleTrigger') {
      return 'Not implemented yet'
    }
    return ''
  }

  exchangeValue(event: any, target: string, form: NgForm) {
    form.control.patchValue({ [target]: event.value ? event.value : event.target.value })
  }

  onIconPickerSelect(icon: string, field: Field, form: NgForm) {
    // prevent the input field to display the ngx-icon-picker default value 'fas fa-user' which does not represent a real icon
    if (icon === 'fas fa-user') {
      form.control.patchValue({ [field.field]: '' })
    } else {
      // the ngx-icon-picker fires an event at loading
      // if the icon is still the same the event is ignored
      if (icon !== field.field) {
        form.control.patchValue({ [field.field]: icon })
      }
    }
  }

  // Reset value of calendar if only one date is selected
  onDatePeriodClose(field: string, form: NgForm) {
    const datePeriod = form.value[field]
    if (Array.isArray(datePeriod) && datePeriod.length == 2 && !datePeriod[1]) {
      form.control.patchValue({ [field]: undefined })
    }
  }

  nullField(field: Field, form: NgForm) {
    form.control.patchValue({ [field.field]: null })
  }

  onSubmit(entityForm: NgForm) {
    this.fields.forEach((field) => {
      if (this.entity && entityForm.value[field.field] === undefined) {
        entityForm.value[field.field] = this.entity[field.field]
      } else if (field.type === 'int' || field.type === 'String' || field.type === 'SelectList') {
        if (entityForm.value[field.field] === '') {
          entityForm.value[field.field] = null
        }
      } else if (field.type === 'Date' || field.type === 'DateTime') {
        if (entityForm.value[field.field] != null) {
          entityForm.value[field.field] = new Date(entityForm.value[field.field]).toISOString()
        }
      } else if (field.type === 'Color') {
        delete entityForm.value['p' + field.field] // because the p-colorpicker duplicates the field with 'p'+<fieldname>
      } else if (entityForm.value[field.field] === undefined) {
        entityForm.value[field.field] = null
      }
    })

    if (this.mainId) {
      entityForm.value['mainId'] = this.mainId
    }

    if (this.ownerType && this.ownerId) {
      entityForm.value['ownerType'] = this.ownerType
      entityForm.value['ownerId'] = this.ownerId
    }
    if (this.$onChange) {
      this.formEnabled = false
      this.$onChange.next(entityForm.value)
    } else {
      this.ref.close(<Field[]>entityForm.value)
    }
  }

  /**
   * allows to re-evaluate defaultvalues of dependent fields if one field is changed
   * dependent fields needs to be configured in evaluateFieldsOnChange property
   * Inputs: event: new value; editedField: Config of edited field; entity: set in update mode, otherwise null; form: entityForm
   */
  evaluateFieldsOnChange(event: any, editedField: Field, entity: Object, form: NgForm) {
    const entityReference = entity ? { id: entity['id'], dtoType: this.configType } : undefined
    // no idea why, but in case of type Reference form.value includes the old values. Thus the new value needs to be saved in formdata
    let formData = form.value
    formData[editedField.field] = event

    // find field configs for given fields that need to be re-evaluated, call eval api for each field and patch form values
    if (editedField.evaluateFieldsOnChange) {
      editedField.evaluateFieldsOnChange
        .map((fieldString) => this.fields.find((field) => field.field === fieldString))
        .forEach((fieldToEvaluate) => {
          if (fieldToEvaluate.defaultValue) {
            if (typeof fieldToEvaluate.defaultValue === 'string' && fieldToEvaluate.defaultValue.startsWith('${')) {
              this.entityService
                .eval(fieldToEvaluate.defaultValue, fieldToEvaluate.type, entityReference, form.value)
                .pipe(
                  tap((response) => form.control.patchValue({ [fieldToEvaluate.field]: response['result'] })),
                  take(1)
                )
                .subscribe()
            } else {
              form.control.patchValue({ [fieldToEvaluate.field]: fieldToEvaluate.defaultValue })
            }
          } else {
            console.error(
              `Can not re-evaluate field. In ${this.configType} config check evaluateFieldsOnChange spelling and defaultValue of fields to be evaluated.`
            )
          }
        })
    }
  }

  revalidate(entityForm: NgForm, control: string) {
    entityForm.controls[control].updateValueAndValidity()
  }

  _isCatalogueEntry(field: Field): boolean {
    return Field.isCatalogueEntry(field)
  }

  _isEntityReference(field: Field): boolean {
    return Field.isEntityReference(field)
  }

  getFloatPattern(decimals: number) {
    return decimals == null
      ? '^-?\\d{1,3}((\\.)?\\d{3})*(,\\d*)?$'
      : '^-?\\d{1,3}((\\.)?\\d{3})*(,\\d{1,' + decimals + '})?$'
  }
}
